/**
 * AtlasShaderWarmModule — WHOLE-SCENE PRE-COMPILE behind the intro (perf
 * frontier 2026-09-08, item 2). The perf-scan profile put 77 % of main-thread
 * self time in program link/introspection after entry: every material
 * variant costs a link the first time it is drawn. The per-batch warm
 * (`warmThenShow`) covers what it is handed; this module sweeps the WHOLE
 * scene with `renderer.compileAsync` at three moments after world entry
 * (3 s, 12 s, 30 s — as streams land) while the intro video or the first
 * seconds hide it. compileAsync only compiles what is not yet compiled, so
 * the later sweeps are cheap. Lever: window.__shaderWarm().
 */
import type { App, IModule } from 'enginex';
import { bus } from '../bus.js';
import { atlas } from '../state.js';

export class AtlasShaderWarmModule implements IModule {
  readonly name = 'AtlasShaderWarm';
  private timers: number[] = [];
  private runs: Array<{ at: number; ms: number; ok: boolean }> = [];
  private worldId: bigint | null = null;

  init(app: App): void {
    const schedule = (): void => {
      for (const t of this.timers) clearTimeout(t);
      this.timers = [3000, 12000, 30000].map((ms) => setTimeout(() => void this.sweep(app, ms), ms) as unknown as number);
    };
    bus.on('atlas:world-entered', () => { this.worldId = atlas.currentWorldId; schedule(); });
    (globalThis as unknown as { __shaderWarm?: () => unknown }).__shaderWarm = () => ({ runs: this.runs.slice() });
  }

  private async sweep(app: App, at: number): Promise<void> {
    if (atlas.currentWorldId === null || atlas.currentWorldId !== this.worldId) return;
    const native = app.renderer.native as unknown as { compileAsync?: (o: unknown, c?: unknown, s?: unknown) => Promise<unknown> };
    if (!native.compileAsync) return;
    const scene = app.world.scene as unknown as object;
    let camera: unknown;
    try { camera = app.renderer.cameras.main; } catch { camera = undefined; }
    const t0 = performance.now();
    try {
      await native.compileAsync(scene, camera, scene);
      this.runs.push({ at, ms: Math.round(performance.now() - t0), ok: true });
    } catch {
      this.runs.push({ at, ms: Math.round(performance.now() - t0), ok: false });
    }
  }

  dispose(): void { for (const t of this.timers) clearTimeout(t); }
}
